import { useMemo } from "react";
import { useDash } from "@/pages/dashboard/ctx";

// HOA words — today's labels verbatim. Used when a tenant payload has no `vocab` yet.
const HOA = {
  terms: {
    org: { one: "Community", many: "Communities" },
    org_kind: { one: "homeowners association", many: "homeowners associations" },
    org_short: { one: "HOA", many: "HOAs" },
    person: { one: "Resident", many: "Residents" },
    unit: { one: "Home", many: "Homes" },
    household: { one: "Household", many: "Households" },
    property: { one: "Property", many: "Properties" },
    staff: { one: "Community manager", many: "Community managers" },
    team: { one: "Management team", many: "Management teams" },
    board: { one: "Board member", many: "Board members" },
    board_group: { one: "The board", many: "The board" },
    mgmt_co: { one: "Management company", many: "Management companies" },
    portal: { one: "Resident portal", many: "Resident portals" },
    request: { one: "Request", many: "Requests" },
    office: { one: "Community office", many: "Community offices" },
    dues: { one: "Dues", many: "Dues" },
    rules: { one: "Rule", many: "Rules" },
    people: { one: "Resident", many: "Residents" },
    amenity: { one: "Amenity", many: "Amenities" },
    booking: { one: "Reservation", many: "Reservations" },
  },
  modules: {
    requests: "Requests", roster: "Residents", roster_title: "Residents", documents: "AI Documents",
    rules: "Rules", contacts: "Trusted Pros", profile: "Community Profile", field_services: "Field Services",
    reservations: "Reservations", events: "Events", renewals: "Renewals", emergency: "Emergency Alerts",
    broadcasts: "Messages", surveys: "Surveys", signatures: "E-Sign", reports: "Reports", public_page: "Public Page",
    conversations: "Conversations", feedback: "Feedback", team: "Management team", portal_requests: "My Requests",
  },
  roles: { hoa_manager: "Community Manager", board_member: "Board Member", regional_manager: "Portfolio Manager", company_admin: "Administrator" },
};

// Acronyms (HOA) keep their case when a lower-case word is asked for.
const lower = (s) => (s && s.toUpperCase() === s ? s : s.toLowerCase());
// "The board" / "The owners" → "board" / "owners" (for "your board", "let the board know").
const stripThe = (s) => s.replace(/^the\s+/i, "");

// Build the vocabulary helper from any payload that carries `industry_pack` + `vocab`
// (a tenant from /tenants, or `me.community` on the resident portal).
export function makeVocab(src) {
  const raw = src?.vocab || {};
  const terms = { ...HOA.terms, ...(raw.terms || {}) };
  const modules = { ...HOA.modules, ...(raw.modules || {}) };
  const roles = { ...HOA.roles, ...(raw.roles || {}) };
  const isHoa = !src?.industry_pack || src.industry_pack.is_default !== false;
  const t = (key, { many = false, lower: lc = false } = {}) => {
    const e = terms[key] || {};
    let s = many ? (e.many || e.one || key) : (e.one || key);
    if (lc) s = lower(s);
    return s;
  };
  const grp = () => lower(stripThe(t("board_group")));
  const art = (w, cap) => ((/^[aeiou]/i.test(w) ? "an" : "a").replace(/^a/, cap ? "A" : "a")) + " " + w;
  return {
    // "a resident" / "an owner" — article follows the word
    a: (k) => art(t(k, { lower: true }), false), A: (k) => art(t(k, { lower: true }), true),
    aGrp: () => art(grp(), false), AGrp: () => art(grp(), true),
    isHoa, pack: src?.industry_pack || null, terms, modules, roles, t,
    one: (k) => t(k), many: (k) => t(k, { many: true }),
    lc: (k) => t(k, { lower: true }), lcs: (k) => t(k, { many: true, lower: true }),
    grp, Grp: () => { const s = grp(); return s.charAt(0).toUpperCase() + s.slice(1); },
    mod: (k, fallback) => modules[k] || fallback || k, role: (k) => roles[k] || k,
  };
}

export function useVocab() {
  const ctx = useDash() || {};
  return useMemo(() => makeVocab(ctx.tenant), [ctx.tenant]);
}

// Module-level accessor for text outside a hook (sub-components, toasts, helpers).
// DashboardLayout / PortalLayout call setActiveVocab() with the active community.
let active = makeVocab(null);
export function setActiveVocab(src) { active = makeVocab(src); return active; }
export const V = {
  get isHoa() { return active.isHoa; },
  // The board seat runs the business in this pack (a roofing company's "Owner") — full manager powers.
  get ownerSeat() { return !!active.pack?.board_seat_full_access; },
  get ownerSeatNote() { return active.pack?.board_seat_note || ""; },
  one: (k) => active.one(k), many: (k) => active.many(k),
  lc: (k) => active.lc(k), lcs: (k) => active.lcs(k),
  grp: () => active.grp(), Grp: () => active.Grp(),
  a: (k) => active.a(k), A: (k) => active.A(k), aGrp: () => active.aGrp(), AGrp: () => active.AGrp(),
  mod: (k, fallback) => active.mod(k, fallback), role: (k) => active.role(k),
};

// The label under a community's household count. HOA keeps the manager's own pick ("Homes" /
// "Units"); every other pack speaks its own word, because "homes" is an HOA-only idea (iter508).
export function unitWord(t) {
  const v = makeVocab(t);
  return v.isHoa ? (t?.unit_label || v.many("unit")) : v.many("household");
}
